/**
 * request/_page 가이드 요청 form island.
 *
 * Mount: `<form data-mount="request-form">`
 *   <input name="query" required>
 *   <textarea name="note">
 *   <button type="submit">
 *   <p data-request-status>  (성공·중복·에러 상태 노출)
 *
 * 흐름:
 *   - submit → POST /api/guide-requests {anon_id, query, note?}
 *   - 200 inserted=true → "요청 받았어요" + form reset + submit disable
 *   - 200 inserted=false → "이미 같은 요청이 있어요" + reset, submit 활성 유지
 *   - 429 → "잠시 후 다시 시도" + submit 활성
 *   - 5xx / network → "잠시 후 다시 시도"
 */

import { getOrCreateAnonId } from "./anonymous-id";

function setStatus(form: HTMLFormElement, text: string, kind: "ok" | "info" | "err"): void {
  const target = form.querySelector<HTMLElement>("[data-request-status]");
  if (!target) return;
  target.textContent = text;
  target.dataset.kind = kind;
}

export function mountRequestForm(form: HTMLFormElement): void {
  const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const data = new FormData(form);
    const query = String(data.get("query") ?? "").trim();
    const note = String(data.get("note") ?? "").trim();
    if (query.length < 2 || query.length > 100) {
      setStatus(form, "서비스명을 2~100자 사이로 입력해주세요", "err");
      return;
    }
    if (note.length > 500) {
      setStatus(form, "메모는 500자 이내로 작성해주세요", "err");
      return;
    }
    if (submit) submit.disabled = true;
    setStatus(form, "전송 중...", "info");

    const body: Record<string, unknown> = {
      anon_id: getOrCreateAnonId(),
      query,
    };
    if (note.length > 0) body.note = note;

    let res: Response;
    try {
      res = await fetch("/api/guide-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } catch {
      setStatus(form, "네트워크 오류 — 잠시 후 다시 시도해주세요", "err");
      if (submit) submit.disabled = false;
      return;
    }

    if (res.status === 429) {
      setStatus(form, "잠시 후 다시 시도해주세요 (분당 3건 제한)", "err");
      if (submit) submit.disabled = false;
      return;
    }
    if (!res.ok) {
      setStatus(form, `서버 오류 (${res.status}) — 잠시 후 다시 시도해주세요`, "err");
      if (submit) submit.disabled = false;
      return;
    }

    const data2 = (await res.json()) as { inserted: boolean; id: number | null };
    if (data2.inserted) {
      setStatus(form, "요청 받았어요. 우선순위에 따라 가이드를 만들어 둘게요.", "ok");
      form.reset();
      // submit 그대로 disabled — 같은 anon_id 같은 query 는 어차피 dedup
    } else {
      setStatus(
        form,
        "이미 같은 요청을 받았어요. 가이드 발행되면 사이트에서 확인 가능합니다.",
        "info",
      );
      form.reset();
      if (submit) submit.disabled = false;
    }
  });
}
